import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { createLocation } from "@/lib/locations.functions";
import { withAuth } from "@/lib/server-fn-auth";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { getCapCopy, type Tier } from "@/lib/location-cap-copy";

export const Route = createFileRoute("/_authenticated/locations/new")({
  component: NewLocationPage,
});

function NewLocationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refresh, switchTo, cap } = useCurrentLocation();
  const [businessName, setBusinessName] = useState("");
  const [slug, setSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const tier: Tier = (cap?.tier as Tier) ?? "none";
  const capReached = cap !== null && !cap.canAddMore;
  const capCopy = capReached ? getCapCopy(tier, cap!.cap, t) : null;

  const onNameChange = (v: string) => {
    setBusinessName(v);
    setSlug(
      v
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 60)
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!businessName.trim() || !slug.trim()) return;
    setSubmitting(true);
    try {
      const { location } = await withAuth(createLocation)({
        data: { business_name: businessName.trim(), slug: slug.trim() },
      });
      toast.success(t('locations.createdToast', { name: location.business_name }));
      await refresh();
      switchTo(location.id);
      navigate({ to: "/dashboard" });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('locations.couldNotCreate'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
        <Link to="/locations" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft size={14} /> {t('locations.backToLocations')}
        </Link>

        {capCopy && (
          <Card className="border-tack-amber/40 bg-tack-amber/5">
            <CardContent className="p-4 text-sm flex items-start gap-3">
              <Lock className="size-4 mt-0.5 shrink-0 text-tack-amber" />
              <div>
                {capCopy.createFooter}{" "}
                <a
                  href={capCopy.bannerHtml.ctaHref}
                  className="text-primary underline underline-offset-2"
                >
                  {capCopy.bannerHtml.cta}
                </a>
                .
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <Plus size={18} /> {t('locations.addNew')}
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {t('locations.addNewIntro')}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">{t('locations.locationName')}</Label>
                <Input
                  id="name"
                  value={businessName}
                  onChange={(e) => onNameChange(e.target.value)}
                  placeholder={t('locations.locationNamePh')}
                  required
                  autoFocus
                  disabled={capReached}
                  maxLength={25}
                />
                <p className="text-xs text-muted-foreground">
                  {t('locations.locationNameHint')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">{t('locations.urlSlug')}</Label>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) =>
                    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))
                  }
                  placeholder={t('locations.urlSlugPh')}
                  required
                  disabled={capReached}
                />
                <p className="text-xs text-muted-foreground">
                  <Trans
                    i18nKey="locations.urlSlugHint"
                    values={{ slug: slug || t('locations.yourSlug') }}
                    components={{ 1: <code /> }}
                  />
                </p>
              </div>

              <Button
                type="submit"
                className="w-full gap-2"
                disabled={submitting || capReached}
              >
                {capReached && <Lock className="size-4" />}
                {submitting ? t('locations.creating') : t('locations.create')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
